import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
  Filter,
  ExcelExport,
  PdfExport,
  FilterType,
} from '@syncfusion/ej2-react-grids';
import { Box, Typography } from '@mui/material';

import {
  useGetWorklistQuery,
  WorklistType,
  WORKLIST_TYPES,
} from '../../../../../services/CaseManagementService';
import { useWorklistSearch, useWorklistNavigation } from './hooks';
import {
  patientTemplate,
  accountTemplate,
  serviceDateTemplate,
  physicianTemplate,
  payerTemplate,
  admitTypeTemplate,
  admitSourceTemplate,
} from './templates';
import { SearchBar } from './components';

export interface WorklistGridProps {
  testId?: string;
}

// Grid settings
const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
const filterSettings = { type: 'Excel' as FilterType };
const sortSettings = { allowUnsort: false };

// Grid container styles
const gridContainerStyles = {
  '& .e-grid': {
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontFamily: 'inherit',
  },
  '& .e-grid .e-gridheader': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .e-grid .e-headercell': {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '14px',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .e-grid .e-altrow': {
    backgroundColor: '#ffffff',
  },
};

/**
 * WorklistGrid - Displays worklist items for case management
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search and navigation
 * - Clean component structure
 */
export const WorklistGrid: React.FC<WorklistGridProps> = ({ testId }) => {
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const typeParam = searchParams.get('type') || '';

  // Validate type parameter
  const type: WorklistType = WORKLIST_TYPES.includes(typeParam as WorklistType)
    ? (typeParam as WorklistType)
    : 'Clinical Review';

  // Fetch worklist data
  const {
    data: worklistItems = [],
    isLoading,
    error,
  } = useGetWorklistQuery(
    { type, center, company },
    {
      skip: !type || !center || !company,
    }
  );

  // Use custom hooks
  const { searchTerm, filteredWorklistItems, handleSearchChange, handleClearSearch } =
    useWorklistSearch(worklistItems);
  const { handleRowSelected } = useWorklistNavigation(company, center);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading worklist...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading worklist. Please try again.</Typography>
      </Box>
    );
  }

  // Empty state
  if (worklistItems.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search by patient, account, physician, payer..."
          />
        </Box>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No cases found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No cases available for {type} in {center}
          </Typography>
        </Box>
      </Box>
    );
  }

  // No search results
  if (filteredWorklistItems.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            placeholder="Search by patient, account, physician, payer..."
          />
        </Box>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No cases match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          placeholder="Search by patient, account, physician, payer..."
        />
      </Box>

      {/* Results Summary */}
      {searchTerm.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredWorklistItems.length} of {worklistItems.length} cases
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredWorklistItems}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
          data-testid={testId}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="patient"
              headerText="Patient"
              width="180"
              textAlign="Left"
              template={patientTemplate}
            />
            <ColumnDirective
              field="account"
              headerText="Account"
              width="120"
              textAlign="Left"
              template={accountTemplate}
            />
            <ColumnDirective
              field="serviceDate"
              headerText="Service Date"
              width="120"
              textAlign="Center"
              template={serviceDateTemplate}
            />
            <ColumnDirective
              field="physician"
              headerText="Physician"
              width="160"
              textAlign="Left"
              template={physicianTemplate}
            />
            <ColumnDirective
              field="payer"
              headerText="Payer"
              width="150"
              textAlign="Left"
              template={payerTemplate}
            />
            <ColumnDirective
              field="admitType"
              headerText="Admit Type"
              width="120"
              textAlign="Center"
              template={admitTypeTemplate}
            />
            <ColumnDirective
              field="admitSource"
              headerText="Admit Source"
              width="130"
              textAlign="Left"
              template={admitSourceTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default WorklistGrid;
